
import React, { useState } from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { type Gift, type Brand, type MarketAction, type ProductSilo } from '@/utils/types';
import TransactionList from './transactions/TransactionList';
import ActionButtons from './transactions/ActionButtons';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';

interface BrandTransactionsProps {
  brand: Brand;
  gifts: Gift[];
  onBack: () => void;
  onAction: (action: MarketAction, selectedGifts: Gift[]) => void;
}

const BrandTransactions: React.FC<BrandTransactionsProps> = ({ 
  brand,
  gifts,
  onBack,
  onAction
}) => {
  const [selectedGifts, setSelectedGifts] = useState<Gift[]>([]);
  const [activeAction, setActiveAction] = useState<MarketAction>('consume');
  const [showExchangeDialog, setShowExchangeDialog] = useState(false);
  const [targetBrand, setTargetBrand] = useState<Brand | null>(null);
  
  const handleSelectGift = (gift: Gift) => {
    const isSelected = selectedGifts.some(g => g.id === gift.id);
    
    if (isSelected) {
      setSelectedGifts(prev => prev.filter(g => g.id !== gift.id));
    } else {
      setSelectedGifts(prev => [...prev, gift]);
    }
  };
  
  const handleSelectAll = () => {
    if (selectedGifts.length === gifts.length) {
      setSelectedGifts([]);
    } else {
      setSelectedGifts([...gifts]);
    }
  };

  const handleExchangeSubmit = () => {
    if (targetBrand) {
      const targetGifts = selectedGifts.map(gift => ({
        ...gift,
        brandId: targetBrand.id,
        id: `exchange-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
      }));
      
      onAction(activeAction, targetGifts);
      setShowExchangeDialog(false);
      setTargetBrand(null);
      setSelectedGifts([]);
      
      toast({
        title: "Exchange successful",
        description: `${selectedGifts.length} gift${selectedGifts.length !== 1 ? 's' : ''} exchanged for ${targetBrand.name}`
      });
    }
  };
  
  const handleAction = () => {
    if (selectedGifts.length === 0) return;
    
    if (activeAction === 'exchange') {
      setShowExchangeDialog(true);
    } else {
      onAction(activeAction, selectedGifts);
    }
  };
  
  const availableBrands: Brand[] = [
    { id: 'starbucks', name: 'Starbucks', logo: '/logos/starbucks.png', silo: 'beverages' as ProductSilo },
    { id: 'amazon', name: 'Amazon', logo: '/logos/amazon.png', silo: 'entertainment' as ProductSilo },
    { id: 'target', name: 'Target', logo: '/logos/target.png', silo: 'convenience' as ProductSilo },
    { id: 'walmart', name: 'Walmart', logo: '/logos/walmart.png', silo: 'convenience' as ProductSilo },
    { id: 'mcdonalds', name: 'McDonald\'s', logo: '/logos/mcdonalds.png', silo: 'restaurants' as ProductSilo },
    { id: 'chipotle', name: 'Chipotle', logo: '/logos/chipotle.png', silo: 'restaurants' as ProductSilo },
    { id: 'netflix', name: 'Netflix', logo: '/logos/netflix.png', silo: 'entertainment' as ProductSilo },
    { id: 'dunkin', name: 'Dunkin', logo: '/logos/dunkin.png', silo: 'beverages' as ProductSilo }
  ].filter(b => b.id !== brand.id);
  
  return (
    <div className="space-y-6">
      <div className="flex items-center">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={onBack}
          className="h-8 w-8 p-0 mr-2"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h3 className="text-lg font-semibold">{brand.name}</h3>
          <p className="text-sm text-muted-foreground">Available Transactions</p>
        </div>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Select Transactions</CardTitle>
        </CardHeader>
        
        <CardContent>
          <TransactionList 
            gifts={gifts}
            selectedGifts={selectedGifts}
            onSelectGift={handleSelectGift}
            onSelectAll={handleSelectAll}
          />
        </CardContent>
        
        <CardFooter className="flex-col space-y-4 border-t pt-4">
          <ActionButtons 
            activeAction={activeAction}
            onActionChange={setActiveAction}
          />
          
          <Button 
            className="w-full bg-givgo-purple hover:bg-givgo-purple/90"
            disabled={selectedGifts.length === 0}
            onClick={handleAction}
          >
            Continue with selected
          </Button>
        </CardFooter>
      </Card>

      <Dialog open={showExchangeDialog} onOpenChange={setShowExchangeDialog}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Exchange for a new brand</DialogTitle>
            <DialogDescription>
              Select a brand to exchange your {selectedGifts.length} gift(s) for a total of ${selectedGifts.reduce((sum, gift) => sum + gift.amount, 0)}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
            <div className="space-y-4">
              <h3 className="font-medium text-sm">Selected GivGo Bucks</h3>
              <div className="border rounded-lg p-4 bg-gray-50 space-y-3 max-h-80 overflow-y-auto">
                {selectedGifts.map(gift => (
                  <div key={gift.id} className="p-3 border rounded-lg bg-white">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium">{brand.name}</p>
                        <p className="text-sm text-muted-foreground">Gift ID: {gift.id.substring(0, 8)}...</p>
                      </div>
                      <p className="text-lg font-bold">${gift.amount}</p>
                    </div>
                  </div>
                ))}
                <div className="pt-2 border-t">
                  <div className="flex justify-between">
                    <p className="font-medium">Total</p>
                    <p className="font-bold">${selectedGifts.reduce((sum, gift) => sum + gift.amount, 0)}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-medium text-sm">Select Brand to Exchange For</h3>
              <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
                <div className="space-y-3">
                  <Select onValueChange={(value) => {
                    const selectedBrand = availableBrands.find(b => b.id === value);
                    if (selectedBrand) {
                      setTargetBrand(selectedBrand);
                    }
                  }}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a brand" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableBrands.map(brand => (
                        <SelectItem key={brand.id} value={brand.id}>
                          <div className="flex items-center">
                            {brand.logo && (
                              <img 
                                src={brand.logo} 
                                alt={brand.name} 
                                className="w-6 h-6 object-contain mr-2"
                              />
                            )}
                            <span>{brand.name}</span>
                            <span className="ml-2 text-xs text-muted-foreground capitalize">({brand.silo})</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {targetBrand && (
                  <div className="p-3 border rounded-lg bg-white">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center">
                        {targetBrand.logo && (
                          <img 
                            src={targetBrand.logo} 
                            alt={targetBrand.name} 
                            className="w-8 h-8 object-contain mr-2"
                          />
                        )}
                        <div>
                          <p className="font-medium">{targetBrand.name}</p>
                          <p className="text-sm text-muted-foreground capitalize">{targetBrand.silo}</p>
                        </div>
                      </div>
                      <p className="text-lg font-bold">${selectedGifts.reduce((sum, gift) => sum + gift.amount, 0)}</p>
                    </div>
                  </div>
                )}
                
                {targetBrand && (
                  <div className="pt-2 border-t">
                    <div className="flex justify-between">
                      <p className="font-medium">Total</p>
                      <p className="font-bold">${selectedGifts.reduce((sum, gift) => sum + gift.amount, 0)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button variant="outline" onClick={() => setShowExchangeDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleExchangeSubmit} 
              disabled={!targetBrand}
              className="bg-givgo-purple hover:bg-givgo-purple/90"
            >
              Confirm Exchange
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrandTransactions;
